import {
  postDemoOpsAdminLeads,
  updateDemoOpsAgentLead,
  postDemoOpsLeadHistoryAudit,
} from "../../../Api/apiUtils";

/** --- Types --- */

export type DemoOpsTabKey = "myleads" | "allleads" | "livedemo";

export interface LeadsListResponse {
  content?: DemoLead[];
  totalPages?: number;
  totalElements?: number;
}

/** Normalize admin/leads body: flat `{ content: [] }` or nested `{ content: { content: [] } }`. */
export function normalizeAdminLeadsResponse(raw: unknown): LeadsListResponse {
  if (raw == null || typeof raw !== "object") {
    return { content: [], totalPages: 1, totalElements: 0 };
  }
  const d = raw as Record<string, unknown>;

  const num = (v: unknown, fallback: number): number =>
    typeof v === "number" && !Number.isNaN(v) ? v : fallback;

  if (Array.isArray(d.content)) {
    const rows = d.content as DemoLead[];
    return {
      content: rows,
      totalPages: Math.max(1, num(d.totalPages, 1)),
      totalElements:
        typeof d.totalElements === "number"
          ? d.totalElements
          : rows.length,
    };
  }

  const inner = d.content as Record<string, unknown> | undefined;
  if (inner && typeof inner === "object" && Array.isArray(inner.content)) {
    const rows = inner.content as DemoLead[];
    return {
      content: rows,
      totalPages: Math.max(
        1,
        num(inner.totalPages, num(d.totalPages, 1))
      ),
      totalElements:
        typeof inner.totalElements === "number"
          ? inner.totalElements
          : typeof inner.numberOfElements === "number"
            ? inner.numberOfElements
            : typeof d.totalElements === "number"
              ? (d.totalElements as number)
              : rows.length,
    };
  }

  const wrapped = d.data as Record<string, unknown> | undefined;
  if (wrapped && typeof wrapped === "object") {
    if (Array.isArray(wrapped.content)) {
      const rows = wrapped.content as DemoLead[];
      return {
        content: rows,
        totalPages: Math.max(1, num(wrapped.totalPages, 1)),
        totalElements:
          typeof wrapped.totalElements === "number"
            ? wrapped.totalElements
            : rows.length,
      };
    }
    const inner2 = wrapped.content as Record<string, unknown> | undefined;
    if (inner2 && Array.isArray(inner2.content)) {
      const rows = inner2.content as DemoLead[];
      return {
        content: rows,
        totalPages: Math.max(1, num(inner2.totalPages, num(wrapped.totalPages, 1))),
        totalElements:
          typeof inner2.totalElements === "number"
            ? inner2.totalElements
            : typeof inner2.numberOfElements === "number"
              ? inner2.numberOfElements
              : rows.length,
      };
    }
  }

  return { content: [], totalPages: 1, totalElements: 0 };
}

export async function loadDemoOpsLeadsPage(
  body: Record<string, unknown>
): Promise<LeadsListResponse> {
  const data = await postDemoOpsAdminLeads(body);
  return normalizeAdminLeadsResponse(data);
}

export interface DemoLead {
  recordId?: string | number | null;
  studentId?: string | number | null;
  classScheduleId?: string | number | null;
  agentId?: string | number | null;
  agentName?: string;
  firstName?: string;
  lastName?: string;
  ageGroup?: string;
  teacherFirstName?: string;
  teacherLastName?: string;
  courseName?: string;
  studentTimezone?: string;
  countryCode?: string;
  mobileNumber?: string;
  agentContactNumber?: string;
  slotDate?: string;
  startTime?: string;
  classDateInTimeZone?: string;
  demoStatus?: string;
  actionPerformed?: string;
  classDeleted?: boolean | null;
  meetType?: string;
  trialType?: string;
  demoCallStatus1?: string;
  demoCallStatus2?: string;
  notConductedReason?: string;
  agentComments?: string;
  prediction?: string;
  userId?: string | number | null;
  studentJoinTime?: string;
  teacherJoinTime?: string;
  country?: string;
  classStatus?: string;
  latestDemoStatus?: string;
  [key: string]: unknown;
}

export interface DemoOpsNavigateState {
  activeTab?: DemoOpsTabKey;
}

export interface DetailViewOpsLocationState extends DemoOpsNavigateState {
  lead?: DemoLead;
  recordId?: string;
  agentId?: string;
  viewName?: "myleads" | "allleads" | "livedemo";
  fromDate?: string;
  toDate?: string;
}

/** --- Filter constants --- */

export type FilterOnField = "" | "callStatus1" | "callStatus2";

export const CONNECTED_REASON_TAGS: string[] = [
  "Interested",
  "Callback requested",
  "Follow-up scheduled",
  "Wants trial another slot",
];

export const NOT_CONNECTED_REASON_TAGS: string[] = [
  "Not Contactable",
  "Not contactable",
  "Busy",
  "Call disconnected",
  "Wrong number",
  "No Answer",
];

export const EXPLICIT_REASON_CHECKBOXES: string[] = [
  ...CONNECTED_REASON_TAGS,
  ...NOT_CONNECTED_REASON_TAGS.filter((r, i, a) => a.indexOf(r) === i),
  "Ringing",
  "Switched off",
  "Interested — needs follow-up",
];

/** "Filter by Tags" — aggregate values (Call Status 1 & 2 filter modal). */
export const DEMO_OPS_FILTER_TAG_CONNECTED = "Connected";
export const DEMO_OPS_FILTER_TAG_NOT_CONNECTED = "Not Connected";

/** "Filter by Reasons" — Connected section (matches product filter UI). */
export const DEMO_OPS_CS_CONNECTED_REASONS: string[] = [
  "Confirmed",
  "Wishes to reschedule",
  "Wishes to cancel",
  "Call back later",
  "Not interested",
];

/** "Filter by Reasons" — Not connected section. */
export const DEMO_OPS_CS_NOT_CONNECTED_REASONS: string[] = [
  "Unanswered",
  "Not Contactable",
  "Wrong Number",
  "Invalid Number",
  "Disconnected after intro",
  "Voicemail",
  "Language Barrier",
];

/** All reason checkboxes (for parsing legacy + new saved filters). */
export const DEMO_OPS_CS_ALL_REASONS: string[] = Array.from(
  new Set([
    ...DEMO_OPS_CS_CONNECTED_REASONS,
    ...DEMO_OPS_CS_NOT_CONNECTED_REASONS,
    ...EXPLICIT_REASON_CHECKBOXES,
  ])
);

export function buildReasonFilter(selection: string[]): string {
  return selection.filter(Boolean).join(",");
}

export type DemoOpsFilterTabKey = "callStatus1" | "callStatus2";

export interface DemoOpsFilterTabState {
  mode: "tags" | "reasons";
  tagConnected: boolean;
  tagNotConnected: boolean;
  reasonChecks: Record<string, boolean>;
}

export function emptyDemoOpsFilterTabState(): DemoOpsFilterTabState {
  return {
    mode: "tags",
    tagConnected: false,
    tagNotConnected: false,
    reasonChecks: {},
  };
}

/** Hydrate one tab from saved `filter` + `filterOn` (parent only stores one active pair). */
export function parseDemoOpsFilterForTab(
  filter: string,
  filterOn: FilterOnField,
  which: DemoOpsFilterTabKey
): DemoOpsFilterTabState {
  const empty = emptyDemoOpsFilterTabState();
  if (filterOn !== which || !`${filter ?? ""}`.trim()) return empty;
  const parts = `${filter}`
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean);
  const isTagOnly =
    parts.length > 0 &&
    parts.every(
      (p) =>
        p === DEMO_OPS_FILTER_TAG_CONNECTED ||
        p === DEMO_OPS_FILTER_TAG_NOT_CONNECTED
    );
  if (isTagOnly) {
    return {
      mode: "tags",
      tagConnected: parts.includes(DEMO_OPS_FILTER_TAG_CONNECTED),
      tagNotConnected: parts.includes(DEMO_OPS_FILTER_TAG_NOT_CONNECTED),
      reasonChecks: {},
    };
  }
  const reasonChecks: Record<string, boolean> = {};
  for (const p of parts) reasonChecks[p] = true;
  return {
    mode: "reasons",
    tagConnected: false,
    tagNotConnected: false,
    reasonChecks,
  };
}

export function buildFilterStringFromTabState(
  s: DemoOpsFilterTabState
): string {
  if (s.mode === "tags") {
    const sel: string[] = [];
    if (s.tagConnected) sel.push(DEMO_OPS_FILTER_TAG_CONNECTED);
    if (s.tagNotConnected) sel.push(DEMO_OPS_FILTER_TAG_NOT_CONNECTED);
    return buildReasonFilter(sel);
  }
  const keys = Object.keys(s.reasonChecks).filter((k) => s.reasonChecks[k]);
  return buildReasonFilter(keys);
}

export const DEMO_STATUS_OPTIONS = [
  "SCHEDULED",
  "COMPLETED",
  "RESCHEDULED",
  "CANCELLED",
  "ONGOING",
  "NO_SHOW",
  "NOT_CONDUCTED",
];

export const DEMO_CALL_OPTIONS = [
  "",
  ...DEMO_OPS_CS_CONNECTED_REASONS,
  ...DEMO_OPS_CS_NOT_CONNECTED_REASONS,
];

export function callStatusSelectOptions(
  current: string | undefined | null
): string[] {
  const v = `${current ?? ""}`.trim();
  const seen = new Set<string>();
  const out: string[] = [];
  for (const o of DEMO_CALL_OPTIONS) {
    if (!seen.has(o)) {
      seen.add(o);
      out.push(o);
    }
  }
  if (v && !seen.has(v)) {
    out.push(v);
  }
  return out;
}

/** Options for “not conducted” on detail / forms (includes filter reason tags + common values). */
export function notConductedReasonSelectOptions(
  current: string | undefined | null
): string[] {
  const v = `${current ?? ""}`.trim();
  const seen = new Set<string>();
  const out: string[] = [];
  const base = [
    "",
    "Student no-show",
    "Teacher no-show",
    "Technical issue",
    "Rescheduled",
    "Cancelled",
    ...EXPLICIT_REASON_CHECKBOXES,
  ];
  for (const o of base) {
    if (!seen.has(o)) {
      seen.add(o);
      out.push(o);
    }
  }
  if (v && !seen.has(v)) {
    out.push(v);
  }
  return out;
}

/** --- Session --- */

export interface DemoOpsSession {
  agentId: string;
  displayName: string;
  agentPhone: string;
  authToken: string;
}

export function getDemoOpsSession(): DemoOpsSession {
  const authToken = localStorage.getItem("authToken") || "";
  let agentId = "";
  let displayName = "";
  let agentPhone = "";
  try {
    const raw = localStorage.getItem("userProfileData");
    if (raw) {
      const p = JSON.parse(raw) as Record<string, unknown>;
      agentId = String(p.uId ?? p.userId ?? "");
      displayName = String(p.studentName ?? p.firstName ?? "");
      agentPhone = String(
        (p.mobileNo as string | undefined) ??
          (p.mobileNumber as string | undefined) ??
          (p.agentContactNumber as string | undefined) ??
          ""
      );
    }
  } catch {
    /* ignore */
  }
  if (!agentId) {
    const uid = localStorage.getItem("userId");
    if (uid) agentId = uid;
  }
  return { agentId, displayName, agentPhone, authToken };
}

export function resolveUpdateAgentId(
  lead: DemoLead,
  viewName: string,
  sessionAgentId: string
): string {
  const v = `${viewName}`.replace(/\s+/g, "").toLowerCase();
  if (v === "allleads") {
    return String(lead.agentId ?? sessionAgentId);
  }
  return sessionAgentId;
}

export function leadDisplayName(lead: DemoLead): string {
  const a = [lead.firstName, lead.lastName].filter(Boolean).join(" ");
  return a || "—";
}

export function teacherDisplayName(lead: DemoLead): string {
  const t = [lead.teacherFirstName, lead.teacherLastName]
    .filter(Boolean)
    .join(" ");
  return t || "—";
}

/** Derived label for the Demo Status column (not the raw API field). */
export function getDisplayDemoStatus(lead: DemoLead): string {
  const meetType = String(lead.meetType ?? "");
  const demoStatus = String(lead.demoStatus ?? "");
  const action = String(lead.actionPerformed ?? "");
  const classDeleted = Boolean(lead.classDeleted);

  if (meetType.includes("NoShow")) return "NOSHOW";
  if (demoStatus === "Class_Ready_For_Scheduling" && classDeleted)
    return "RESCHEDULED";
  if (demoStatus === "SCHEDULED" && action === "teacher_demo_cancelclass")
    return "NOT CONDUCTED";
  if (demoStatus === "SCHEDULED" && action === "student_trial_cancelclass")
    return "CANCELLED";
  return demoStatus || "—";
}

/** --- Persist + audit --- */

export type LeadSaveTouched = "demo" | "call1" | "call2" | "misc";

const AUDIT_FIELDS: (keyof DemoLead)[] = [
  "demoStatus",
  "demoCallStatus1",
  "demoCallStatus2",
  "agentComments",
];

function computeLatestDemoStatus(
  after: DemoLead,
  touched: LeadSaveTouched
): string {
  if (touched === "call1")
    return String(after.demoCallStatus1 ?? after.demoStatus ?? "");
  if (touched === "call2")
    return String(after.demoCallStatus2 ?? after.demoStatus ?? "");
  if (touched === "demo") return String(after.demoStatus ?? "");
  return String(
    after.latestDemoStatus ?? after.demoStatus ?? after.demoCallStatus1 ?? ""
  );
}

export async function persistLeadWithAudit(
  before: DemoLead,
  after: DemoLead,
  opts: {
    agentIdSave: string;
    displayName: string;
    sessionAgentId: string;
    touched: LeadSaveTouched;
  }
): Promise<void> {
  await updateDemoOpsAgentLead({
    classId: String(after.classScheduleId ?? ""),
    latestDemoStatus: computeLatestDemoStatus(after, opts.touched),
    DemoCallStatus1: String(after.demoCallStatus1 ?? ""),
    DemoCallStatus2: String(after.demoCallStatus2 ?? ""),
    notConductedReason: String(after.notConductedReason ?? ""),
    comments: String(after.agentComments ?? ""),
    agentId: opts.agentIdSave,
  });

  const sid = String(after.studentId ?? "");
  if (!sid) return;

  for (const field of AUDIT_FIELDS) {
    const oldVal = String(before[field] ?? "");
    const newVal = String(after[field] ?? "");
    if (oldVal === newVal) continue;
    try {
      await postDemoOpsLeadHistoryAudit({
        id: sid,
        field: String(field),
        user: opts.displayName,
        user_id: opts.sessionAgentId,
        old_value: oldVal,
        new_value: newVal,
      });
    } catch {
      /* optional gateway */
    }
  }
}
